import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDemo } from '../contexts/DemoContext';
import DemoCard from '../components/demos/DemoCard';
import DemoDetailsModal from '../components/demos/DemoDetailsModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ArrowLeftIcon, FunnelIcon, CalendarIcon } from '@heroicons/react/24/outline';

export default function DemosList() {
  const { demoBookings, loading } = useDemo();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDemo, setSelectedDemo] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { demoId } = useParams();
  
  // Check if we're showing a modal based on URL
  const isModalOpen = location.pathname.includes('/demos/');

  const filteredDemos = demoBookings.filter(booking => {
    const customerName = booking.payload?.customer?.name || '';
    const demoId = booking.id || '';
    const status = booking.payload?.status || 'scheduled';
    
    const matchesSearch = 
      demoId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatus = statusFilter === 'all' || status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Set selected demo when demoId changes in URL
  useEffect(() => {
    if (demoId && demoBookings.length > 0) {
      const demo = demoBookings.find(d => d.id === demoId);
      if (demo) {
        setSelectedDemo(demo);
      }
    }
  }, [demoId, demoBookings]);
  
  const handleDemoClick = (demo) => {
    setSelectedDemo(demo);
    navigate(`/demos/${demo.id}`, { state: { background: location } });
  };
  
  const handleCloseModal = () => {
    navigate(-1);
    setSelectedDemo(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {/* Demo Details Modal */}
      {isModalOpen && selectedDemo && (
        <DemoDetailsModal 
          demo={selectedDemo}
          onClose={handleCloseModal}
        />
      )}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">My Demo Bookings</h1>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search demos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div>
              <div className="relative">
                <select
                  className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium">{filteredDemos.length}</span> of <span className="font-medium">{demoBookings.length}</span> demos
              </p>
            </div>
          </div>
        </div>

        {/* Demos List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {filteredDemos.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No demo bookings found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter to find what you\'re looking for.'
                  : 'You haven\'t scheduled any demos yet.'}
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => navigate('/book-demo')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Schedule New Demo
                </button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredDemos.map((demo) => (
                <div key={demo.id} onClick={() => handleDemoClick(demo)}>
                  <DemoCard 
                    demo={demo} 
                    onClick={() => handleDemoClick(demo)}
                  />
                </div>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
